import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Popconfirm, Select, Space, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createTextBlock, deleteTextBlock, listTextBlocks, updateTextBlock } from "./api";
import { textBlockKinds } from "./format";

/** Textbausteine: intro and closing texts of offers and position long texts. */
const TextBlocksTab = ({ meta }) => {
  const { t } = useTranslation("dashboard");
  const kinds = textBlockKinds(t);
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    listTextBlocks()
      .then(res => setBlocks(res.data.data))
      .catch(generalErrorAlert);

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { title: editing.title, kind: editing.kind, text: editing.text };
      if (editing._id) await updateTextBlock(editing._id, body);
      else await createTextBlock(body);
      setEditing(null);
      load();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async block => {
    try {
      await deleteTextBlock(block._id);
      load();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const columns = [
    { title: t("Title"), dataIndex: "title", key: "title", mobile: "title" },
    {
      title: t("Kind"),
      key: "kind",
      mobile: "badge",
      render: (_, b) => <Tag>{kinds[b.kind]}</Tag>
    },
    {
      title: t("Text"),
      key: "text",
      mobile: "full",
      render: (_, b) => (
        <span className="mat-wrap">
          {b.text.length > 140 ? `${b.text.slice(0, 140)}…` : b.text}
        </span>
      )
    },
    {
      title: t("Actions"),
      key: "actions",
      render: (_, b) => (
        <Space wrap>
          {meta.permissions.update ? (
            <Button size="small" onClick={() => setEditing(b)}>
              {t("Edit")}
            </Button>
          ) : null}
          {meta.permissions.delete ? (
            <Popconfirm title={t("Delete this text block?")} onConfirm={() => remove(b)}>
              <Button size="small" danger>
                {t("Delete")}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageToolbar
        actions={
          meta.permissions.create ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing({ title: "", kind: "intro", text: "" })}
            >
              {t("New text block")}
            </Button>
          ) : null
        }
      />
      <ResponsiveTable
        rowKey="_id"
        columns={columns}
        dataSource={blocks}
        mobileAvatar={false}
        pagination={false}
      />
      <Modal
        rootClassName="materials-page"
        open={Boolean(editing)}
        title={editing?._id ? t("Edit text block") : t("New text block")}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={busy}
        destroyOnClose
      >
        {editing ? (
          <Space direction="vertical" className="mat-full">
            <Input
              placeholder={t("Title")}
              value={editing.title}
              onChange={e => setEditing(prev => ({ ...prev, title: e.target.value }))}
            />
            <Select
              className="mat-full"
              value={editing.kind}
              onChange={kind => setEditing(prev => ({ ...prev, kind }))}
              options={Object.entries(kinds).map(([value, label]) => ({ value, label }))}
            />
            <Input.TextArea
              autoSize={{ minRows: 5, maxRows: 14 }}
              value={editing.text}
              onChange={e => setEditing(prev => ({ ...prev, text: e.target.value }))}
            />
          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default TextBlocksTab;
